"use client";
import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import makeEvent from "@/hooks/eventing/makeEvent";
import useElementEvents from "@/hooks/eventing/useElementEvents";
import { EVENTS } from "@/hooks/eventing/types/events";
import { CommercePath, useBuildCommerceUrl } from "@/hooks/useBuildCommerceUrl";
import { MonarchDataConsumer } from "@/monarch/MonarchDataConsumer";
import type { MultiCartTestResult } from "@/monarch/rules/multiCart/types";
import {
  CartData,
  firstEnrichedProgramInfo,
  formatCurrency,
  getProgramInfoForItem,
} from "@/utils/cartUtils";
import { getCorrelationID } from "@/utils/tagular/helpers";
import {
  formatCourseName,
  createProductViewEventData,
  createElementViewEventData,
  createConversionEventData,
  hasProgram,
  getIndividualCourses,
  getProgramCourses,
  getDisplayedCourses,
  MULTI_PURCHASE_CART_EVENT_CONFIGS,
} from "./utils";
import { ConversionTracked } from "@/hooks/eventing/types/core.conversions.ConversionTracked.v2";

interface CartPopupProps {
  isOpen: boolean;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  cartData: CartData;
  cartItemCount: number;
}

export default function CartPopup({
  isOpen,
  onMouseEnter,
  onMouseLeave,
  cartData,
  cartItemCount,
}: CartPopupProps) {
  const [showAllCourses, setShowAllCourses] = useState<Record<string, boolean>>(
    {},
  );

  const items = useMemo(() => cartData?.items ?? [], [cartData]);
  const subtotal = useMemo(() => cartData?.subtotal ?? 0, [cartData]);
  const total = useMemo(() => cartData?.total ?? 0, [cartData]);
  const checkoutUrl = useBuildCommerceUrl(CommercePath.PaymentRedirect);

  // Brand information comes from CommerceTools lineItems[].variant.attributes['brand-text']
  // For now using mock brand data, will be replaced with real CommerceTools data

  // Use Tagular's dedicated correlation ID system for conversion tracking
  const correlationId = getCorrelationID();

  // Element event tracking for checkout CTA - no static webElement, will use dynamic text
  const { elementClicked: checkoutCtaClicked } = useElementEvents({
    webElement: MULTI_PURCHASE_CART_EVENT_CONFIGS.cartPopupButton,
  });

  useEffect(() => {
    if (isOpen && items.length > 0) {
      // Fire events for each item using makeEvent (simpler approach)
      items.forEach((item, index) => {
        // Product Viewed event
        const productViewedEvent = makeEvent(
          createProductViewEventData(
            item,
            index,
            getProgramInfoForItem(cartData, item)?.brand,
            correlationId,
          ),
        );
        productViewedEvent(EVENTS.ProductViewed)();

        // Element Viewed event
        const elementViewedEvent = makeEvent(
          createElementViewEventData(item, index, correlationId),
        );
        elementViewedEvent(EVENTS.ElementViewed)();
      });
    }
  }, [isOpen, items, cartData, correlationId]);

  // Track conversion when going to checkout
  const handleCheckoutClick = (buttonText: string) => {
    // Track element click with dynamic text
    checkoutCtaClicked({
      correlationId,
      webElement: {
        ...MULTI_PURCHASE_CART_EVENT_CONFIGS.cartPopupButton,
        text: buttonText, // Use dynamic text from Monarch
      },
    });

    // Track conversion using Tagular correlation ID
    const conversionEvent = createConversionEventData(
      items,
      firstEnrichedProgramInfo(cartData)?.brand,
      correlationId,
      "cart_checkout",
    );

    const event = makeEvent<ConversionTracked, EVENTS>(conversionEvent);
    event<ConversionTracked>(EVENTS.ConversionTracked)();

    // Add small delay to ensure event is sent before navigation
    setTimeout(() => {
      window.location.href = checkoutUrl;
    }, 150);
  };

  if (!isOpen) return null;

  // Determine if we have programs in the cart - allow mix of programs and courses
  const hasProgramItems = hasProgram(items);
  const individualCourses = getIndividualCourses(items, cartData.bundleInfoMap);

  return (
    <div
      className={[
        "bg-white border border-gray-200 shadow-lg z-50 rounded",

        // Desktop/tablet: anchored to the cart icon
        "md:absolute md:right-0 md:top-full md:mt-2",

        // Mobile: center it and keep it inside the viewport
        "fixed md:absolute left-1/2 md:left-auto -translate-x-1/2 md:translate-x-0 top-16 md:top-auto",

        // Widths: clamp to viewport on mobile; keep original widths on md+
        cartItemCount === 0
          ? "md:w-56 w-[clamp(14rem,92vw,22rem)]"
          : "md:w-96 w-[clamp(18rem,92vw,26rem)]",

        // Safety: never exceed viewport width
        "max-w-[calc(100vw-1rem)]",

        // Margin for mobile gutters
        "mx-2 md:mx-0",

        // Cap overall height and clip extra - scrolling for entire modal
        "max-h-[85vh] overflow-y-auto overflow-x-hidden overscroll-contain",
      ].join(" ")}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      style={{ zIndex: "calc(infinity)" }}
    >
      <div className={cartItemCount === 0 ? "p-0" : "p-4 md:p-6"}>
        <div
          className={`cart-section w-full cart-section-styled flex flex-col justify-center ${
            cartItemCount === 0 ? "min-h-[80px] text-center" : "min-h-[200px]"
          }`}
        >
          <div className="flex justify-between items-center">
            <h2
              className={`font-bold ${cartItemCount === 0 ? "mb-1 mt-0 text-[20px]" : "mb-3 mt-0 text-lg"}`}
            >
              {cartItemCount === 0 ? "Your cart is empty" : "In your cart"}
            </h2>
            {cartItemCount > 0 && (
              <div className="flex items-center gap-2 mb-0.5">
                <span className="text-base text-black">
                  <span className="font-bold">{cartItemCount}</span>{" "}
                  {cartItemCount === 1 ? "item" : "items"}
                </span>
                <div className="w-px h-4 bg-gray-300"></div>
                <button
                  onClick={() => onMouseLeave()}
                  className="w-6 h-6 flex items-center justify-center hover:opacity-50 transition-opacity"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="25"
                    viewBox="0 0 24 25"
                    fill="none"
                  >
                    <g opacity="0.35">
                      <path
                        d="M12 2.5C6.47 2.5 2 6.97 2 12.5C2 18.03 6.47 22.5 12 22.5C17.53 22.5 22 18.03 22 12.5C22 6.97 17.53 2.5 12 2.5ZM17 16.09L15.59 17.5L12 13.91L8.41 17.5L7 16.09L10.59 12.5L7 8.91L8.41 7.5L12 11.09L15.59 7.5L17 8.91L13.41 12.5L17 16.09Z"
                        fill="black"
                      />
                    </g>
                  </svg>
                </button>
              </div>
            )}
          </div>
          {cartItemCount > 0 && (
            <>
              <div className="border-b border-gray-200 mb-2 pt-4"></div>

              {/* Scrollable LIST ONLY */}
              <div className="flex-1 space-y-4 mb-6 pr-2">
                {/* Show Programs first if they exist */}
                {hasProgramItems &&
                  Object.entries(cartData.bundleInfoMap).map(
                    ([bundleId, bundleInfo]) => {
                      if (!bundleId) return null;
                      const programCourses = getProgramCourses(items, bundleId);
                      const displayedCourses = getDisplayedCourses(
                        items,
                        bundleId,
                        showAllCourses,
                      );
                      const totalPrice = programCourses.reduce(
                        (n, { totalPrice, price }) =>
                          totalPrice ? n + totalPrice : n + price,
                        0,
                      );
                      const originalPrice = programCourses.reduce(
                        (n, { originalPrice, price }) =>
                          originalPrice ? n + originalPrice : n + price,
                        0,
                      );

                      return (
                        <div
                          key={bundleId}
                          className="flex justify-between items-start"
                        >
                          <div className="flex-1">
                            <p className="font-semibold text-black mb-0 mt-3 text-base font-inter">
                              {formatCourseName(
                                bundleInfo.title,
                                bundleInfo.partners[0]?.key,
                              )}
                            </p>
                            <div className="flex justify-between items-center mt-1">
                              <span className="text-gray-600 text-base">
                                Verified Certificate
                              </span>
                              <div>
                                {originalPrice > totalPrice && (
                                  <span className="text-gray-500 line-through text-sm mr-2">
                                    {formatCurrency(originalPrice)}
                                  </span>
                                )}
                                <span className="text-black text-base font-normal">
                                  {formatCurrency(totalPrice)}
                                </span>
                              </div>
                            </div>
                            <p className="text-black text-sm mt-2 mb-2 font-bold">
                              This program includes {programCourses.length}{" "}
                              courses:
                            </p>
                            {programCourses.length > 0 && (
                              <button
                                onClick={() =>
                                  setShowAllCourses((prev) => ({
                                    ...prev,
                                    [bundleId]: !prev[bundleId],
                                  }))
                                }
                                className="text-[#00688D] underline text-sm flex items-center mb-2"
                              >
                                {showAllCourses ? "Show less" : "Show all"}
                                <span className="ml-1 text-black no-underline">
                                  {showAllCourses ? (
                                    <svg
                                      className="w-5 h-5"
                                      fill="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path d="M7.41 15.41L12 10.83l4.59 4.58L18 14l-6-6-6 6z" />
                                    </svg>
                                  ) : (
                                    <svg
                                      className="w-5 h-5"
                                      fill="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6z" />
                                    </svg>
                                  )}
                                </span>
                              </button>
                            )}
                            <div className="space-y-1">
                              {displayedCourses.map((course, index) => (
                                <div
                                  key={course.id || index}
                                  className="flex items-start"
                                >
                                  <span className="text-black mr-2">•</span>
                                  <span className="text-black text-sm">
                                    {formatCourseName(course.courseName)}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      );
                    },
                  )}

                {/* Show Individual Courses */}
                {individualCourses.map((item) => (
                  <div
                    key={item.id}
                    className="flex justify-between items-start"
                  >
                    <div className="flex-1">
                      <p className="font-semibold text-black mb-0 mt-3 text-base">
                        {formatCourseName(item.courseName, item.brand)}
                      </p>
                      <div className="flex justify-between items-center mt-1">
                        <span className="text-gray-600 text-base">
                          Verified Certificate
                        </span>
                        <span className="text-black text-base font-normal">
                          {formatCurrency(item.price)}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="border-t border-gray-200 pt-4">
                <div className="flex justify-between items-center mb-4">
                  <span className="font-semibold text-base text-black">
                    Total
                  </span>
                  <div className="flex items-center">
                    {subtotal > total && (
                      <span className="text-gray-500 line-through text-sm mr-2">
                        {formatCurrency(subtotal)}USD
                      </span>
                    )}
                    <span className="font-semibold text-base text-black">
                      {formatCurrency(total)}
                    </span>
                  </div>
                </div>
                <MonarchDataConsumer<MultiCartTestResult>
                  experimentKey="useMultiCart"
                  render={(testResult) => {
                    const buttonText =
                      testResult?.data?.goToCartCtaText ?? "Go to cart";
                    return (
                      <Button
                        variant="brand"
                        className="w-full"
                        size="default"
                        onClick={() => handleCheckoutClick(buttonText)}
                      >
                        {buttonText}
                      </Button>
                    );
                  }}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
